import React from "react";
import { useLanguage } from "./LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";

// ─── Shared SVG icons ─────────────────────────────────────────────────────────
const Icon = ({ d, viewBox = "0 0 24 24" }) => (
  <svg width="28" height="28" viewBox={viewBox} fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const STROKE = { fill: "none", stroke: "#2563eb", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" };

// ─── Shared section components ────────────────────────────────────────────────
function SectionLabel({ children, dark }) {
  if (dark) {
    return (
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 20 }}>
        {children}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", padding: "5px 14px", background: "rgba(37,99,235,0.07)", borderRadius: 100, border: "1px solid rgba(37,99,235,0.15)" }}>
        <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%", display: "inline-block" }} />
        {children}
      </div>
    </div>
  );
}

function SectionHero({ eyebrow, title, titleEm, lead }) {
  return (
    <div style={{ padding: "72px 40px 48px", maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
      <SectionLabel>{eyebrow}</SectionLabel>
      <h2 style={{
        fontFamily: "'new-science','DM Sans',sans-serif",
        fontSize: "clamp(28px,3.5vw,44px)", fontWeight: 700,
        lineHeight: 1.1, letterSpacing: "-0.02em", color: "#0f172a", margin: "0 0 20px",
      }}>
        {title}<em style={{ color: "#2563eb", fontStyle: "italic" }}>{titleEm}</em>
      </h2>
      <p style={{ fontSize: 16, color: "#64748b", lineHeight: 1.65, margin: 0 }}>{lead}</p>
    </div>
  );
}

const PRODUKT_CSS = `
  .produkt-card-grid { display: grid; gap: 16px; }
  .produkt-card-grid-3 { grid-template-columns: repeat(3, 1fr); }
  .produkt-card-grid-2 { grid-template-columns: repeat(2, 1fr); }
  .produkt-sub-grid-2  { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
  .produkt-sub-grid-3  { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 640px) {
    .produkt-card-grid-3, .produkt-card-grid-2 { grid-template-columns: repeat(2, 1fr); }
    .produkt-sub-grid-2, .produkt-sub-grid-3   { grid-template-columns: 1fr; }
    .produkt-card-wrap  { padding: 0 16px 40px; }
    .produkt-sub-wrap   { padding: 24px 16px 40px; }
  }
`;

function CardGrid({ cols = 3, children }) {
  return (
    <div className="produkt-card-wrap" style={{ padding: "0 40px 56px" }}>
      <div className={`produkt-card-grid produkt-card-grid-${cols}`} style={{ maxWidth: 1040, margin: "0 auto" }}>
        {React.Children.map(children, (child) => (
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 22px" }}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ icon, title, body, accent }) {
  return (
    <div>
      {icon && <div style={{ marginBottom: 16 }}>{icon}</div>}
      <div style={{ fontWeight: 700, fontSize: 15, color: accent || "#0f172a", marginBottom: 8, fontFamily: "'new-science','DM Sans',sans-serif" }}>{title}</div>
      <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: 0 }}>{body}</p>
    </div>
  );
}

function SubSection({ label, title, body, children }) {
  return (
    <div className="produkt-sub-wrap" style={{ maxWidth: 1040, margin: "0 auto", padding: "40px 40px 56px", textAlign: "center" }}>
      {label && <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>{label}</div>}
      <h3 style={{
        fontFamily: "'new-science','DM Sans',sans-serif",
        fontSize: "clamp(20px,2vw,28px)", fontWeight: 700,
        color: "#0f172a", margin: "0 0 24px", lineHeight: 1.1,
      }}>{title}</h3>
      {body && <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, margin: "0 auto 36px", maxWidth: 640 }}>{body}</p>}
      <div style={{ textAlign: "left" }}>{children}</div>
    </div>
  );
}

function SectionDivider() {
  return <div style={{ borderTop: "2px solid #e2e8f0", margin: "0 40px" }} />;
}

// ─── Main ProduktPage ─────────────────────────────────────────────────────────
export default function ProduktPage({ onSignup, onGoToAudit }) {
  const { t } = useLanguage();
  const p = (k) => t(`produkt.${k}`);
  usePageMeta({ title: `${t("publicNav.produkt")} — GeoRev.ai` });

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", color: "#0f172a", background: "#f8fafc" }}>
      <style>{PRODUKT_CSS}</style>

      {/* ══════════════════════════════════════════════════════
          01 — SICHTBARKEITS-SCORE
          ══════════════════════════════════════════════════════ */}
      <div id="sichtbarkeit" style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", scrollMarginTop: 70 }}>
        <SectionHero
          eyebrow={p("s1Eyebrow")}
          title={p("s1Title")}
          titleEm={p("s1TitleEm")}
          lead={p("s1Lead")}
        />
        <CardGrid cols={3}>
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>}
            title={p("s1c1t")} body={p("s1c1b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><circle cx="12" cy="12" r="2"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/></svg>}
            title={p("s1c2t")} body={p("s1c2b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
            title={p("s1c3t")} body={p("s1c3b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><polyline points="3 21 3 4"/><polyline points="3 21 21 21"/><polyline points="5 17 9 12 13 15 19 7"/></svg>}
            title={p("s1c4t")} body={p("s1c4b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
            title={p("s1c5t")} body={p("s1c5b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
            title={p("s1c6t")} body={p("s1c6b")}
          />
        </CardGrid>
      </div>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════
          02 — POSITIONSANALYSE
          ══════════════════════════════════════════════════════ */}
      <div id="position" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", scrollMarginTop: 70 }}>
        <SectionHero
          eyebrow={p("s2Eyebrow")}
          title={p("s2Title")}
          titleEm={p("s2TitleEm")}
          lead={p("s2Lead")}
        />
        <CardGrid cols={3}>
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>}
            title={p("s2c1t")} body={p("s2c1b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><polyline points="3 21 3 4"/><polyline points="3 21 21 21"/><polyline points="5 17 9 12 13 15 19 7"/></svg>}
            title={p("s2c2t")} body={p("s2c2b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>}
            title={p("s2c3t")} body={p("s2c3b")}
          />
        </CardGrid>

        <SubSection label={p("s2subLabel")} title={p("s2subTitle")}>
          <div className="produkt-sub-grid-2">
            {[
              { tk: "s2d1t", dk: "s2d1b" },
              { tk: "s2d2t", dk: "s2d2b" },
              { tk: "s2d3t", dk: "s2d3b" },
              { tk: "s2d4t", dk: "s2d4b" },
            ].map((c, i) => (
              <div key={i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "24px 24px" }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, fontFamily: "'new-science','DM Sans',sans-serif" }}>{p(c.tk)}</div>
                <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: 0 }}>{p(c.dk)}</p>
              </div>
            ))}
          </div>
        </SubSection>
      </div>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════
          03 — WAHRNEHMUNG & TONALITÄT
          ══════════════════════════════════════════════════════ */}
      <div id="wahrnehmung" style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", scrollMarginTop: 70 }}>
        <SectionHero
          eyebrow={p("s3Eyebrow")}
          title={p("s3Title")}
          titleEm={p("s3TitleEm")}
          lead={p("s3Lead")}
        />
        <CardGrid cols={3}>
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>}
            title={p("s3c1t")} body={p("s3c1b")} accent="#16a34a"
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><circle cx="12" cy="12" r="10"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>}
            title={p("s3c2t")} body={p("s3c2b")} accent="#64748b"
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>}
            title={p("s3c3t")} body={p("s3c3b")} accent="#dc2626"
          />
        </CardGrid>

        <SubSection label={p("s3subLabel")} title={p("s3subTitle")}>
          <div className="produkt-sub-grid-3">
            {[
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" {...STROKE}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, tk: "s3w1t", dk: "s3w1b" },
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" {...STROKE}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>, tk: "s3w2t", dk: "s3w2b" },
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" {...STROKE}><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg>, tk: "s3w3t", dk: "s3w3b" },
            ].map((c, i) => (
              <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24 }}>
                <div style={{ marginBottom: 12 }}>{c.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, fontFamily: "'new-science','DM Sans',sans-serif" }}>{p(c.tk)}</div>
                <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: 0 }}>{p(c.dk)}</p>
              </div>
            ))}
          </div>
        </SubSection>
      </div>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════
          04 — QUELLENANALYSE
          ══════════════════════════════════════════════════════ */}
      <div id="quellen" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", scrollMarginTop: 70 }}>
        <SectionHero
          eyebrow={p("s4Eyebrow")}
          title={p("s4Title")}
          titleEm={p("s4TitleEm")}
          lead={p("s4Lead")}
        />
        <CardGrid cols={2}>
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="8 12 11 15 16 9"/></svg>}
            title={p("s4c1t")} body={p("s4c1b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>}
            title={p("s4c2t")} body={p("s4c2b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
            title={p("s4c3t")} body={p("s4c3b")}
          />
          <Card
            icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>}
            title={p("s4c4t")} body={p("s4c4b")}
          />
        </CardGrid>

        <SubSection label={p("s4subLabel")} title={p("s4subTitle")}>
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {[p("s4thEngine"), p("s4thCitation"), p("s4thRelevanz")].map(h => (
                    <th key={h} style={{ padding: "12px 20px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Perplexity", p("s4r1b"), p("s4r1c")],
                  ["ChatGPT",   p("s4r2b"), p("s4r2c")],
                  ["Gemini",    p("s4r3b"), p("s4r3c")],
                  ["Claude",    p("s4r4b"), p("s4r4c")],
                  ["Copilot",   p("s4r5b"), p("s4r5c")],
                ].map(([a, b, c], i) => (
                  <tr key={i} style={{ borderBottom: i < 4 ? "1px solid #e2e8f0" : "none" }}>
                    <td style={{ padding: "14px 20px", fontWeight: 600 }}>{a}</td>
                    <td style={{ padding: "14px 20px", color: "#64748b" }}>{b}</td>
                    <td style={{ padding: "14px 20px", color: "#64748b" }}>{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SubSection>
      </div>

      <SectionDivider />

      {/* ══════════════════════════════════════════════════════
          05 — WETTBEWERBSVERGLEICH
          ══════════════════════════════════════════════════════ */}
      <div id="wettbewerb" style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", scrollMarginTop: 70 }}>
        <SectionHero
          eyebrow={p("s5Eyebrow")}
          title={p("s5Title")}
          titleEm={p("s5TitleEm")}
          lead={p("s5Lead")}
        />
        <CardGrid cols={3}>
          <Card icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}           title={p("s5c1t")} body={p("s5c1b")} />
          <Card icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>} title={p("s5c2t")} body={p("s5c2b")} />
          <Card icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}                                    title={p("s5c3t")} body={p("s5c3b")} />
          <Card icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2C5.3 13.7 5.3 9.8 7.8 7.3"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.5 2.5 2.5 6.4 0 8.9"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/></svg>} title={p("s5c4t")} body={p("s5c4b")} />
          <Card icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>} title={p("s5c5t")} body={p("s5c5b")} />
          <Card icon={<svg width="28" height="28" viewBox="0 0 24 24" {...STROKE}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}                   title={p("s5c6t")} body={p("s5c6b")} />
        </CardGrid>

        <SubSection label={p("s5subLabel")} title={p("s5subTitle")}>
          <div className="produkt-sub-grid-3">
            {[
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" {...STROKE}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, tk: "s5u1t", dk: "s5u1b", b1: "s5u1b1", b2: "s5u1b2" },
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" {...STROKE}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>, tk: "s5u2t", dk: "s5u2b", b1: "s5u2b1", b2: "s5u2b2" },
              { icon: <svg width="24" height="24" viewBox="0 0 24 24" {...STROKE}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>, tk: "s5u3t", dk: "s5u3b", b1: "s5u3b1", b2: "s5u3b2" },
            ].map((c, i) => (
              <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24 }}>
                <div style={{ marginBottom: 12 }}>{c.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, fontFamily: "'new-science','DM Sans',sans-serif" }}>{p(c.tk)}</div>
                <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: "0 0 12px" }}>{p(c.dk)}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {[c.b1, c.b2].map((bk, j) => (
                    <li key={j} style={{ fontSize: 12, color: "#2563eb", fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#2563eb", flexShrink: 0 }} />{p(bk)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </SubSection>
      </div>

      {/* ══════════════════════════════════════════════════════
          CTA
          ══════════════════════════════════════════════════════ */}
      <div style={{ background: "#0f172a", padding: "80px 40px", textAlign: "center" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20, padding: "5px 14px", background: "rgba(255,255,255,0.08)", borderRadius: 100, border: "1px solid rgba(255,255,255,0.15)" }}>
            <span style={{ width: 5, height: 5, background: "rgba(255,255,255,0.5)", borderRadius: "50%", display: "inline-block" }} />
            {p("ctaEyebrow")}
          </div>
          <h2 style={{
            fontFamily: "'new-science','DM Sans',sans-serif",
            fontSize: "clamp(28px,3.5vw,44px)", fontWeight: 700,
            color: "#fff", lineHeight: 1.1, letterSpacing: "-0.02em", margin: "0 0 16px",
          }}>
            {p("ctaTitle")}
          </h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", lineHeight: 1.65, margin: "0 0 36px" }}>
            {p("ctaLead")}
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={onGoToAudit}
              style={{
                padding: "14px 28px", borderRadius: 9, border: "none",
                background: "#2563eb", color: "#fff",
                fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {p("ctaBtn")}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
